/**
 * Confidence arithmetic (docs/02 §2). Integer basis points only; axes compose by
 * minimum, never product. Discounts apply to reward-derived gains only — callers
 * must never call this on a cost term (fx_fee, interest_risk).
 */

import { asCents, roundHalfUp, type Cents } from '../money';
import type { ConfidenceTier } from './types';

/**
 * docs/02 §2 source_confidence_bps table. confidence_tier's declared order in
 * docs/01-SCHEMA.sql mirrors that table's rows one-for-one.
 */
export function sourceConfidenceBps(tier: ConfidenceTier): number {
  switch (tier) {
    case 'account_agreement':
      return 10000;
    case 'current_statement':
      return 9800;
    case 'logged_in_account':
      return 9500;
    case 'network_guide':
      return 9000;
    case 'issuer_public_page':
      return 8000;
    case 'user_reported':
      return 7000;
    case 'third_party':
      return 5000;
    case 'inferred':
      return 3500;
    case 'unverified':
      return 0;
  }
}

/** discounted = round_half_up(cents * bps / 10000). bps must be non-negative. */
export function applyBps(cents: Cents, bps: number): Cents {
  if (bps < 0) throw new Error('bps must be non-negative');
  return asCents(roundHalfUp(cents * BigInt(bps), 10_000n));
}

/**
 * Multiplicative combination of two independent bps axes (e.g. a tier multiplier
 * and the additional 7000bps category-uncertainty factor, docs/03 §2/§5).
 */
export function bpsProduct(a: number, b: number): number {
  if (a < 0 || b < 0) throw new Error('bps must be non-negative');
  return Number(roundHalfUp(BigInt(a) * BigInt(b), 10_000n));
}

/** Weakest-link combination (docs/02 §2: min, not product). */
export function minBps(...values: readonly number[]): number {
  if (values.length === 0) throw new Error('minBps requires at least one value');
  return values.reduce((min, v) => (v < min ? v : min));
}

/**
 * docs/02 §2 staleness_multiplier_bps: within TTL -> 10000; decays linearly to a
 * floor of 5000 at 2x TTL; never below the floor, never zero.
 */
export function stalenessMultiplierBps(ageDays: number, ttlDays: number): number {
  if (ttlDays <= 0) return 5000;
  if (ageDays <= ttlDays) return 10000;
  if (ageDays >= ttlDays * 2) return 5000;
  const decay = roundHalfUp(BigInt(ageDays - ttlDays) * 5000n, BigInt(ttlDays));
  return 10000 - Number(decay);
}
